import cn from 'classnames';
import React, { memo } from 'react';

import { Badge } from '@sravni/react-design-system/lib/Badge';
import { Card } from '@sravni/react-design-system/lib/Card';
import { Space } from '@sravni/react-design-system/lib/Space';
import { Text } from '@sravni/react-design-system/lib/Typography';

import { UNBREAKABLE_SEPARATOR } from '@src/constants/unbreakableSeparator';
import { formatMoney } from '@src/helpers/formatMoney';

import { DAYS_IN_WEEK } from '../../config';
import { formatDateWithOffset } from '../../lib';

import {
  LONG_AMOUNT_COEFFICENT_CHANGE,
  LONG_TERM_FIRST_RANGE_COEFFICIENT,
  LONG_TERM_SECOND_RANGE_COEFFICIENT,
} from './FullDealEntryPointFormCalculations.config';
import styles from './FullDealEntryPointFormCalculations.module.scss';
import { calculateLongDebt, calculateShortDebt } from './lib';

interface Props {
  term: number;
  amount: number;
}

export const FullDealEntryPointFormCalculations = memo(({ term, amount }: Props) => {
  const withoutOverpayment = term < 22;
  const isShowShortDebt = term < 34;

  const amountWithOverpayment = calculateShortDebt(amount, term);
  const repaymentDate = formatDateWithOffset(term - 1);

  if (isShowShortDebt) {
    return (
      <Card className={styles.container} color="dark" size={16}>
        <Space className={styles.content}>
          <Text size={12}>К возврату</Text>

          <Space size={4} justify="start" align="center">
            {withoutOverpayment && (
              <Badge className={styles.badge} color="green" variant="primary" text={<Text size={14}>0%</Text>} />
            )}

            <Text
              size={14}
              className={cn({
                'h-color-G100': withoutOverpayment,
              })}
              strong
            >
              {withoutOverpayment ? formatMoney(amount) : formatMoney(amountWithOverpayment)}
            </Text>

            {withoutOverpayment && (
              <Text size={14} className={styles.decoratedText}>
                {formatMoney(amountWithOverpayment, { withSymbol: false })}
              </Text>
            )}
          </Space>
        </Space>

        <Space className={styles.rightContent}>
          <Text size={12}>Погасить до</Text>
          <Text size={14} strong className={styles.alignRight}>
            {repaymentDate}
          </Text>
        </Space>
      </Card>
    );
  }

  const termByWeeks = Math.round(term / DAYS_IN_WEEK);
  const calculations = calculateLongDebt(
    amount,
    termByWeeks,
    amount > LONG_AMOUNT_COEFFICENT_CHANGE ? LONG_TERM_SECOND_RANGE_COEFFICIENT : LONG_TERM_FIRST_RANGE_COEFFICIENT,
  );
  // @todo: Пока не удаляем, скорее всего потом пригодится
  // termByWeeks > TERM_SLIDER_MIN_THIRD_RANGE && termByWeeks < TERM_SLIDER_MAX_THIRD_RANGE
  //   ? calculateLongDebt(amount, termByWeeks, LONG_TERM_FIRST_RANGE_COEFFICIENT)
  //   : calculateLongDebt(amount, termByWeeks, LONG_TERM_SECOND_RANGE_COEFFICIENT);

  return (
    <Card className={styles.longDebtContainer} color="dark" size={16}>
      <Space className={styles.content}>
        <Text size={12}>Платеж раз в 2 недели</Text>
        <Text size={14} className={styles.alignRight}>
          <Text as="span" strong>
            {formatMoney(calculations)}
          </Text>
          {UNBREAKABLE_SEPARATOR}
          <Text as="span" size={10} className="h-color-D40">
            c {formatDateWithOffset(14)}
          </Text>
        </Text>
      </Space>

      <Space className={styles.content}>
        <Text size={12}>Погасить до</Text>
        <Text size={14} strong className={styles.alignRight}>
          {repaymentDate}
        </Text>
      </Space>
    </Card>
  );
});

FullDealEntryPointFormCalculations.displayName = 'FullDealEntryPointFormCalculations';
